import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { useUpgradePreview } from '../hooks/queries/useUpgradePreview';
import { useCancelSubscription } from '../hooks/mutations/useCancelSubscription';
import { useResumeSubscription } from '../hooks/mutations/useResumeSubscription';
import { useUpgradeSubscription } from '../hooks/mutations/useUpgradeSubscription';
import { useDowngradeSubscription } from '../hooks/mutations/useDowngradeSubscription';
import { useCancelDowngrade } from '../hooks/mutations/useCancelDowngrade';
import { useCreateCheckout } from '../hooks/mutations/useCreateCheckout';
import { useCreateOneshotPayment } from '../hooks/mutations/useCreateOneshotPayment';
import { formatHours, nextRenewalLabel, planLabel } from '../utils/formatters';
import { sessionGuidance, SESSION_GUIDANCE_MESSAGE } from '../utils/sessionQuota';
import { Card, Button } from '../components/ui';
import ConfirmDialog from '../components/ConfirmDialog';
import Layout from '../components/Layout';
import Header from '../components/Header';
import PaymentHistory from '../components/PaymentHistory';
import type { User } from '../types';

interface BillingPageProps {
  user: User;
}

export default function BillingPage({ user }: BillingPageProps) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // アップグレードの確認だけは他の確認と分けて持つ。日割り差額の試算（非同期）を
  // ダイアログの表示中に反映する必要があり、生成時点で固定される message では表せないため。
  const [upgradeTarget, setUpgradeTarget] = useState<{ type: string; name: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    variant?: 'primary' | 'danger';
    onConfirm: () => void;
  } | null>(null);
  const [oneshotHours, setOneshotHours] = useState(3);
  const [oneshotSessions, setOneshotSessions] = useState(3);

  const cancelSubscription = useCancelSubscription();
  const resumeSubscription = useResumeSubscription();
  const upgradeSubscription = useUpgradeSubscription();
  const downgradeSubscription = useDowngradeSubscription();
  const cancelDowngrade = useCancelDowngrade();
  const createCheckout = useCreateCheckout();
  const createOneshotPayment = useCreateOneshotPayment();
  // 確認ダイアログを開いている間だけ試算する（閉じている間は planType が null で実行されない）
  const upgradePreview = useUpgradePreview(upgradeTarget?.type ?? null);
  // 解約・解約取消・プラン変更・チェックアウト作成のいずれかが進行中ならボタンを無効化する
  // （チェックアウト成功時は Stripe へ画面遷移するため、その間も操作を止める）
  const loading =
    cancelSubscription.isPending ||
    resumeSubscription.isPending ||
    upgradeSubscription.isPending ||
    downgradeSubscription.isPending ||
    cancelDowngrade.isPending ||
    createCheckout.isPending ||
    createOneshotPayment.isPending;

  // 解約予約中かどうかと満了日。満了日は cancel_effective_at のみを使う
  // （billing_cycle_end は処理時刻起点で書かれ、決済側の期間終了日とズレるため）。
  const isCancelScheduled = user.plan.cancel_at_period_end === true;
  const cancelEffectiveAt = user.plan.cancel_effective_at
    ? new Date(user.plan.cancel_effective_at).toLocaleDateString('ja-JP')
    : null;

  // ダウングレード予約中かどうかと適用日。適用日は取得できていない場合があり、
  // その場合も予約自体は成立しているため日付だけを伏せて案内する。
  const pendingPlanType = user.plan.pending_plan_type ?? null;
  const pendingEffectiveAt = user.plan.pending_plan_effective_at
    ? new Date(user.plan.pending_plan_effective_at).toLocaleDateString('ja-JP')
    : null;
  // 解約予約中はダウングレード予約の案内を出さない。満了日に解約されるのに次期の
  // プラン変更も案内されると帰結が矛盾して見えるため、解約予約を優先して表示する
  // （予約そのものの破棄は別途対応。解約を取り消せばこの案内は戻る）。
  const isDowngradeScheduled = pendingPlanType !== null && !isCancelScheduled;
  // 契約中の次回更新日。解約予約中は下の解約予約の案内（満了日）を出すため表示しない
  const nextRenewal = nextRenewalLabel(user.plan);

  const ONESHOT_PRICE_PER_HOUR = 120;
  const ONESHOT_PRICE_PER_SESSION = 100;
  // 無制限プランは時間・回数を消費しないので、ワンショット購入のカードを出さない
  // （基準はバックエンドの購入可否・付与の判定と同じ hours_limit = -1）。
  const isUnlimited = user.plan.hours_limit === -1;
  // 回数の購入はライト・スタンダードのみ（フリーは月の時間が少なく回数を足しても使えないため）
  const canBuySessions = user.plan.plan_type === 'lite' || user.plan.plan_type === 'standard';
  // 回数切れで月枠の時間が残っているとき、時間の購入カードから次の手段へ誘導する
  // （buy_sessions はライト・スタンダードのみ返るので、誘導先の回数購入カードは必ず表示されている）
  const guidance = sessionGuidance(user.plan);

  // Billing の表示仕様: 1時間以上は「N時間」表記（toFixed なし）、1時間未満は「N分」の分のみ表記
  const formatPlanHours = (hours: number): string =>
    formatHours(hours, { unit: '時間', minutesOnlyBelowOneHour: true });

  const plans = [
    {
      type: 'free',
      name: '無料',
      price: 0,
      hours: 0.25,
      description: '試しに使ってみたい方向け',
    },
    {
      type: 'lite',
      name: 'ライト',
      price: 500,
      hours: 9,
      description: '月に数回遊ぶ方向け',
    },
    {
      type: 'standard',
      name: 'スタンダード',
      price: 1000,
      hours: 18,
      description: '定期的に遊ぶ方向け',
      recommended: true,
    },
    {
      type: 'unlimited',
      name: '無制限',
      price: 4000,
      hours: -1,
      description: 'たくさん遊ぶ方向け',
    },
  ];

  const handleSubscribe = (planType: string) => {
    // 無料プランは購入対象外。プランボタン側で常に disabled にしているため、
    // このガードは UI 上到達不能（多層防御として残置）。disabled 条件を変更する際は
    // この不変条件も併せて見直すこと。
    if (planType === 'free') {
      setError('無料プランは選択できません');
      return;
    }
    const plan = plans.find((p) => p.type === planType);
    if (!plan) return;
    setError('');

    // 契約中に上位プランを選んだ場合はアップグレード（即時適用・日割り課金）へ、
    // 下位プランを選んだ場合はダウングレード（次期適用の予約）へ振り分ける。
    // 判定は表示用の価格表で行うが、これは動線を分けるためのものであり、
    // 課金の可否はサーバ側が有効なプランで再判定する。
    // 無料プランからの新規契約は現行どおり Checkout に流す。現在プランが価格表に
    // 見つからない場合も Checkout に落ちる。契約中のユーザーがここへ落ちるのはミラー乖離
    // （DB が free を指すのに Stripe 側の契約が生存）などの例外時のみで、二重契約は
    // バックエンドの受付ガードが拒否する。フロントはそのサーバ文言を表示する。
    const currentPlan = plans.find((p) => p.type === user.plan.plan_type);
    const isPaidUser = user.plan.plan_type !== 'free' && !!currentPlan;

    if (isPaidUser && plan.price > currentPlan.price) {
      handleUpgrade(plan.type, plan.name);
      return;
    }

    if (isPaidUser && plan.price < currentPlan.price) {
      handleDowngrade(plan.type, plan.name);
      return;
    }

    // 定期課金: Stripe Checkout（subscription mode）へ直接遷移する。
    // 金額・カード入力・最終確認は Stripe のホストページ側で行うため、中間の確認ダイアログは設けない。
    // 成功時は hook が決済ページURLへ window.location 遷移する。
    // 失敗時はサーバの detail（受付ガードの案内文言等）を優先表示する。無い場合のみ既定文言に
    // フォールバックする（アップグレード / ダウングレードの onError と同じ作法）。
    createCheckout.mutate(plan.type, {
      onError: (err) => {
        const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
        setError(detail || '決済ページの作成に失敗しました');
      },
    });
  };

  // アップグレードは Stripe のホストページを経由せずサーバ側で即時適用するため、
  // 適用タイミングと日割り課金を操作前にここで示す（Unit 006 で規約4条4項・FAQと帰結を突き合わせ確定）。
  // 金額は開いてから試算するため、揃うまで確認ボタンを押させない。
  const handleUpgrade = (planType: string, planName: string) => {
    setError('');
    setUpgradeTarget({ type: planType, name: planName });
  };

  const handleConfirmUpgrade = () => {
    if (!upgradeTarget) return;
    // 成功時は完了画面へ遷移し、請求された差額を提示する（リロードしないため、
    // Hook 側で AuthContext とお支払い履歴を整合させている）。
    // 失敗時のみダイアログを閉じてエラー表示。サーバ側の文言（同一プラン /
    // ダウングレード / 支払い失敗）をそのまま見せる。
    upgradeSubscription.mutate(upgradeTarget.type, {
      onSuccess: (data) => {
        setUpgradeTarget(null);
        navigate('/billing/plan-changed', {
          state: {
            planType: data.plan_type,
            chargedAmount: data.charged_amount,
            hoursLimit: data.hours_limit,
          },
        });
      },
      onError: (err) => {
        setUpgradeTarget(null);
        const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
        setError(detail || 'プランの変更に失敗しました');
      },
    });
  };

  const handleDowngrade = (planType: string, planName: string) => {
    // ダウングレードは次回更新日から適用される。今の期間の条件が変わらないこと、
    // 日割りの返金がないこと、適用前なら取り消せることを操作前に示す（Unit 006 で規約4条4項・FAQと帰結を突き合わせ確定）。
    setConfirmAction({
      title: `${planName}プランへの変更`,
      message: (
        <>
          {planName}プランは<strong>次回更新日から</strong>適用されます。<br />
          今の期間は<strong>現在のプランのまま</strong>ご利用いただけます（上限も使用済みの時間も変わりません）。<br />
          日割りでの返金はありません。適用前であれば予約を取り消せます。
        </>
      ),
      confirmLabel: 'この内容で予約する',
      variant: 'primary',
      onConfirm: () => {
        // 成功時は Hook が window.location.reload()。失敗時のみダイアログを閉じてエラー表示。
        // サーバ側の文言（同一プラン / 上位プラン / 予約済み）をそのまま見せる。
        downgradeSubscription.mutate(planType, {
          onError: (err) => {
            setConfirmAction(null);
            const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
            setError(detail || 'プランの変更予約に失敗しました');
          },
        });
      },
    });
  };

  const handleCancelDowngrade = () => {
    setError('');
    // 成功時は Hook が window.location.reload()。失敗時のみエラー表示。
    cancelDowngrade.mutate(undefined, {
      onError: (err) => {
        const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
        setError(detail || '変更予約の取り消しに失敗しました');
      },
    });
  };

  const handleCancelSubscription = () => {
    setError('');
    setConfirmAction({
      title: 'サブスクリプションの解約',
      message: (
        <>
          解約しても、<strong>課金済み期間の満了日まで</strong>はこれまでどおりご利用いただけます。満了日を過ぎると自動的に<strong>無料プランに戻ります</strong>。<br />
          月の途中で解約しても<strong>日割りでの返金はありません</strong>。解約は満了日までであれば取り消して継続できます。<br />
          本当に解約しますか？
        </>
      ),
      confirmLabel: '解約する',
      variant: 'danger',
      onConfirm: () => {
        // 成功時は Hook が window.location.reload()。失敗時のみダイアログを閉じてエラー表示。
        cancelSubscription.mutate(undefined, {
          onError: () => {
            setConfirmAction(null);
            setError('サブスクリプションのキャンセルに失敗しました');
          },
        });
      },
    });
  };

  const handleResumeSubscription = () => {
    setError('');
    // 成功時は Hook が window.location.reload()。失敗時のみエラー表示。
    resumeSubscription.mutate(undefined, {
      onError: () => setError('解約の取り消しに失敗しました'),
    });
  };

  const handleOneshotPurchase = () => {
    setError('');
    // ワンショット: Stripe Checkout（payment mode）へ直接遷移する（最終確認は Stripe 側）。
    createOneshotPayment.mutate({ hours: oneshotHours }, {
      onError: () => setError('決済ページの作成に失敗しました'),
    });
  };

  const handleSessionPurchase = () => {
    setError('');
    createOneshotPayment.mutate({ sessions: oneshotSessions }, {
      onError: () => setError('決済ページの作成に失敗しました'),
    });
  };

  return (
    <Layout>
      <Header user={user} />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-4">プラン変更・課金</h1>
        <p className="text-gray-600 mb-8">
          現在のプラン: <span data-testid="billing-current-plan" className="font-bold text-primary">{planLabel(user.plan.plan_type)}</span>
          {nextRenewal && (
            <>
              <br />
              <span data-testid="billing-next-renewal" className="text-sm">{nextRenewal}</span>
            </>
          )}
        </p>

        {/* 解約導線は現在プラン表示の直下・エラーアラートの手前に配置し、解約失敗時のエラーをボタン近傍で視認できるようにする（有料プラン時のみ表示）。
            解約予約中は同じ位置を「予約中の案内 + 取り消し導線」に差し替える */}
        {user.plan.plan_type !== 'free' && (
          isCancelScheduled ? (
            <div data-testid="billing-cancel-scheduled-section" className="mb-8">
              <p className="text-sm text-gray-700 mb-2">
                {cancelEffectiveAt
                  ? <>解約を受け付けています。<strong>{cancelEffectiveAt}</strong>まではこれまでどおりご利用いただけます。</>
                  : <>解約を受け付けています。課金済み期間の満了日まではこれまでどおりご利用いただけます。</>}
                <br />
                満了後は自動的に無料プランに戻ります。
              </p>
              <Button
                variant="secondary"
                onClick={handleResumeSubscription}
                disabled={loading}
              >
                解約を取り消す
              </Button>
            </div>
          ) : (
            <div data-testid="billing-cancel-section" className="mb-8">
              <Button
                variant="secondary"
                onClick={handleCancelSubscription}
                disabled={loading}
              >
                サブスクリプションをキャンセル
              </Button>
              <p className="text-sm text-gray-600 mt-2">
                解約しても満了日まで利用でき、満了後に無料プランに戻ります
              </p>
            </div>
          )
        )}

        {/* ダウングレード予約中の案内と取り消し導線。解約予約中は表示しない（上の分岐で解約を優先） */}
        {isDowngradeScheduled && (
          <div data-testid="billing-downgrade-scheduled-section" className="mb-8">
            <p className="text-sm text-gray-700 mb-2">
              {pendingEffectiveAt
                ? <><strong>{planLabel(pendingPlanType)}</strong>プランへの変更を<strong>{pendingEffectiveAt}</strong>に予約中です。</>
                : <><strong>{planLabel(pendingPlanType)}</strong>プランへの変更を次回更新日に予約中です。</>}
              <br />
              それまでは現在のプランのままご利用いただけます。
            </p>
            <Button
              variant="secondary"
              onClick={handleCancelDowngrade}
              disabled={loading}
            >
              変更予約を取り消す
            </Button>
          </div>
        )}

        {error && (
          <div role="alert" className="bg-danger-bg text-danger-fg px-4 py-3 rounded-field mb-6">
            {error}
          </div>
        )}

        <div id="plan-select" className="grid md:grid-cols-4 gap-6 mb-12">
          {plans.map((plan) => (
            <Card
              key={plan.type}
              className={`relative ${
                plan.recommended ? 'border-2 border-primary' : ''
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm">
                  おすすめ
                </div>
              )}

              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <div className="text-3xl font-bold text-primary mb-4">
                ¥{plan.price.toLocaleString()}
                <span className="text-sm text-gray-600">/月</span>
              </div>

              <ul className="space-y-2 mb-6 text-sm text-gray-600">
                <li>
                  ✓ 月{formatPlanHours(plan.hours)}
                </li>
                <li className="text-xs text-gray-500">{plan.description}</li>
              </ul>

              <Button
                onClick={() => handleSubscribe(plan.type)}
                // 無料プランは選択対象外のため常に disabled。予約中のプランも同じプランへの
                // 二重予約になるため disabled（現在プラン一致・loading と論理和。順序非依存）
                disabled={
                  loading ||
                  user.plan.plan_type === plan.type ||
                  plan.type === 'free' ||
                  pendingPlanType === plan.type
                }
                className="w-full"
              >
                {/* ラベルは評価順が重要: 「現在プラン一致」を free 判定より必ず先に評価する
                    （無料が現在プランのユーザーでは両条件が真になるが「現在のプラン」が正）。
                    予約中の表示は free 判定より後（予約先に free は入らないため順序は無害だが、
                    現在プラン一致より後にすることで「現在のプラン」を優先する） */}
                {user.plan.plan_type === plan.type
                  ? '現在のプラン'
                  : plan.type === 'free'
                  ? '選択不可'
                  : pendingPlanType === plan.type
                  ? '変更予約中'
                  : '選択'}
              </Button>
            </Card>
          ))}
        </div>

        {!isUnlimited && (
        <Card className="border-2 border-primary/30 bg-background">
          <h2 className="text-xl font-bold mb-4">ワンショット課金</h2>
          <p className="text-gray-600 mb-6">
            サブスク不要！必要な時に必要な分だけ購入できます。
          </p>

          {guidance && (
            <div className="text-sm text-gray-700 mb-6">
              <p>{SESSION_GUIDANCE_MESSAGE[guidance]}</p>
              {/* 同じページ内のハッシュ移動（パスは変わらないので ScrollToTop は発火しない） */}
              <a
                href={guidance === 'buy_sessions' ? '#session-purchase' : '#plan-select'}
                className="text-primary hover:underline"
              >
                {guidance === 'buy_sessions' ? '回数の追加購入へ' : 'プランを選ぶ'}
              </a>
            </div>
          )}

          <div className="bg-white rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-700 font-medium">購入時間:</span>
              <span className="text-2xl font-bold text-primary">{oneshotHours}時間</span>
            </div>

            <div className="flex items-center gap-2 mb-6" role="group" aria-label="購入時間の選択">
              {[1, 3, 5, 10].map((hours) => (
                <button
                  key={hours}
                  type="button"
                  onClick={() => setOneshotHours(hours)}
                  aria-pressed={oneshotHours === hours}
                  className={`flex-1 py-3 rounded-lg font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    oneshotHours === hours
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {hours}時間
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between py-4 border-t border-b mb-6">
              <span className="text-gray-600">合計金額:</span>
              <span className="text-3xl font-bold text-primary">
                ¥{(oneshotHours * ONESHOT_PRICE_PER_HOUR).toLocaleString()}
              </span>
            </div>

            <Button
              size="lg"
              onClick={handleOneshotPurchase}
              loading={loading}
              disabled={loading}
              className="w-full"
            >
              {loading ? '処理中...' : `${oneshotHours}時間分を購入する`}
            </Button>
          </div>

          <div className="text-sm text-gray-600 space-y-1">
            <p>• 1時間あたり ¥{ONESHOT_PRICE_PER_HOUR}</p>
            <p>• 購入した時間は無期限で利用可能</p>
            <p>• サブスクリプションとは別に加算されます</p>
          </div>
        </Card>
        )}

        {canBuySessions && (
          <Card id="session-purchase" className="border-2 border-primary/30 bg-background mt-8">
            <h2 className="text-xl font-bold mb-4">回数の追加購入</h2>
            <p className="text-gray-600 mb-6">
              時間は残っているのに回数が足りないときに、書き起こし回数だけを追加できます。
            </p>

            <div className="bg-white rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-700 font-medium">購入回数:</span>
                <span className="text-2xl font-bold text-primary">{oneshotSessions}回</span>
              </div>

              <div className="flex items-center gap-2 mb-6" role="group" aria-label="購入回数の選択">
                {[1, 3, 5, 10].map((sessions) => (
                  <button
                    key={sessions}
                    type="button"
                    onClick={() => setOneshotSessions(sessions)}
                    aria-pressed={oneshotSessions === sessions}
                    className={`flex-1 py-3 rounded-lg font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      oneshotSessions === sessions
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {sessions}回
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between py-4 border-t border-b mb-6">
                <span className="text-gray-600">合計金額:</span>
                <span className="text-3xl font-bold text-primary">
                  ¥{(oneshotSessions * ONESHOT_PRICE_PER_SESSION).toLocaleString()}
                </span>
              </div>

              <Button
                size="lg"
                onClick={handleSessionPurchase}
                loading={loading}
                disabled={loading}
                className="w-full"
              >
                {loading ? '処理中...' : `${oneshotSessions}回分を購入する`}
              </Button>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p>• 1回あたり ¥{ONESHOT_PRICE_PER_SESSION}</p>
              <p>• 購入した回数は無期限で利用可能</p>
              <p>• 月の回数を使い切った後に1回ずつ使われます</p>
            </div>
          </Card>
        )}

        <PaymentHistory userEmail={user.email} userName={user.display_name} />

      </main>

      {/* アップグレードの確認ダイアログ。請求される日割り差額を提示してから確定させる */}
      <ConfirmDialog
        open={upgradeTarget !== null}
        title={`${upgradeTarget?.name ?? ''}プランへの変更`}
        message={
          <>
            <strong>今すぐ</strong>{upgradeTarget?.name}プランの上限に切り替わります。<br />
            {upgradePreview.isLoading ? (
              <span data-testid="upgrade-preview-loading">
                残り期間の差額を計算しています…<br />
              </span>
            ) : upgradePreview.data?.amount != null && upgradePreview.data.amount > 0 ? (
              <span data-testid="upgrade-preview-amount">
                残り期間の差額<strong>¥{upgradePreview.data.amount.toLocaleString()}</strong>
                が<strong>日割りで請求</strong>されます。<br />
              </span>
            ) : (
              // 試算できなかった場合も操作は続けられる。金額を伏せ、日割りである事実だけを示す
              <span data-testid="upgrade-preview-unavailable">
                残り期間の差額が<strong>日割りで請求</strong>されます。<br />
              </span>
            )}
            今月すでに使った時間はそのまま引き継がれます（上限だけが上がります）。
          </>
        }
        confirmLabel="プランを変更する"
        variant="primary"
        loading={upgradeSubscription.isPending}
        confirmDisabled={upgradePreview.isLoading}
        onConfirm={handleConfirmUpgrade}
        onClose={() => setUpgradeTarget(null)}
      />

      {/* 操作前の確認ダイアログ（申し込み・購入・解約） */}
      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction?.title || '確認'}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        variant={confirmAction?.variant}
        loading={cancelSubscription.isPending || downgradeSubscription.isPending}
        onConfirm={() => confirmAction?.onConfirm()}
        onClose={() => setConfirmAction(null)}
      />
    </Layout>
  );
}
